// Iter 145 — Inline confidence-trend sparkline for the Schedule page.
//
// Fetches GET /api/schedule/league/{league_id}/confidence-trend on
// mount, renders an SVG sparkline of daily hit-rate alongside a
// twin avg-confidence trace and a small drift summary.
//
// Strict no-fabrication: empty days render at 0% with greyed dots so
// the user can immediately tell "no settled samples that day" apart
// from "low hit-rate that day".

import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const W = 220, H = 48, PAD = 4;

const buildPath = (points, key, max) => {
  if (!points.length || !max) return "";
  const stepX = (W - PAD * 2) / Math.max(points.length - 1, 1);
  return points
    .map((p, i) => {
      const x = PAD + i * stepX;
      const v = Number(p[key] || 0);
      const y = H - PAD - (v / max) * (H - PAD * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};

const ConfidenceTrendSparkline = ({ leagueId, days = 30 }) => {
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/schedule/league/${leagueId}/confidence-trend?days=${days}`)
      .then((r) => { if (!cancelled) setTrend(r.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leagueId, days]);

  if (loading) {
    return (
      <div
        className="text-[11px] text-muted-foreground italic"
        data-testid={`trend-loading-${leagueId}`}
      >Loading confidence trend…</div>
    );
  }
  if (error || !trend) {
    return (
      <div
        className="text-[11px] text-muted-foreground italic"
        data-testid={`trend-empty-${leagueId}`}
      >No trend data</div>
    );
  }

  const points = trend.points || [];
  // Compute drift: last 7 days vs full window.
  const recent = points.slice(-7);
  const settledRecent = recent.reduce((s, p) => s + (p.settled || 0), 0);
  const wonRecent = recent.reduce((s, p) => s + (p.won || 0), 0);
  const recentHr = settledRecent
    ? Math.round((wonRecent * 100.0) / settledRecent * 10) / 10
    : 0;
  const drift = settledRecent
    ? Math.round((recentHr - trend.overall_hit_rate) * 10) / 10
    : 0;

  // Use a 100% scale for both hit-rate and confidence so they're
  // comparable in the same SVG.
  const max = 100;
  const hrPath = buildPath(points, "hit_rate", max);
  const cfPath = buildPath(points, "avg_confidence", max);

  // Dot color hints — zero-sample days = greyed.
  const stepX = (W - PAD * 2) / Math.max(points.length - 1, 1);

  const DriftIcon = drift > 1 ? TrendingUp : drift < -1 ? TrendingDown : Minus;
  const driftClass =
    drift > 1
      ? "text-emerald-300"
      : drift < -1
      ? "text-rose-300"
      : "text-muted-foreground";

  return (
    <div
      className="flex items-center gap-3 flex-wrap"
      data-testid={`trend-sparkline-${leagueId}`}
    >
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="shrink-0"
        role="img"
        aria-label="Confidence trend sparkline"
      >
        <defs>
          <linearGradient id={`hrGrad-${leagueId}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* gridline at 50% */}
        <line x1={PAD} x2={W - PAD} y1={H / 2} y2={H / 2} stroke="rgb(255 255 255 / 0.06)" strokeDasharray="2 3" />
        {/* avg confidence (cyan) underline */}
        <path d={cfPath} fill="none" stroke="rgb(34 211 238 / 0.6)" strokeWidth="1.3" />
        {/* hit rate (emerald) */}
        <path d={`${hrPath} L${W - PAD},${H - PAD} L${PAD},${H - PAD} Z`} fill={`url(#hrGrad-${leagueId})`} />
        <path d={hrPath} fill="none" stroke="rgb(16 185 129)" strokeWidth="1.6" />
        {/* dots */}
        {points.map((p, i) => {
          const x = PAD + i * stepX;
          const y = H - PAD - (Number(p.hit_rate || 0) / max) * (H - PAD * 2);
          const settled = p.settled || 0;
          return (
            <circle
              key={i}
              cx={x}
              cy={y}
              r={settled ? 1.4 : 0.9}
              fill={settled ? "rgb(16 185 129)" : "rgb(120 120 120 / 0.4)"}
            />
          );
        })}
      </svg>

      <div className="text-[11px] leading-tight">
        <div className="flex items-center gap-1.5">
          <span className="text-emerald-300 font-bold font-tabular" data-testid={`trend-overall-${leagueId}`}>
            {trend.overall_hit_rate}%
          </span>
          <span className="text-muted-foreground">overall · {trend.total_settled} settled</span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-cyan-300 font-tabular">{trend.overall_avg_confidence}%</span>
          <span className="text-muted-foreground">avg conf</span>
          {settledRecent > 0 && (
            <span
              className={`inline-flex items-center gap-0.5 ml-1 font-bold ${driftClass}`}
              data-testid={`trend-drift-${leagueId}`}
              title="7-day drift vs full window"
            >
              <DriftIcon className="w-3 h-3" />
              {drift > 0 ? "+" : ""}{drift}pp
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfidenceTrendSparkline;
